import PropTypes, { number } from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Badge,
    Alert
} from "reactstrap";

//Import Breadcrumb
import { withTranslation } from "react-i18next";
import { 
    getAllEvents, 
    getAllOrganizer, 
    getBookingByEventId, 
    getForumBySlug,
    getAllBookingByDateRange,
    getAllBookingByOrganizer,
    getEventVenuesBySlug,
    getAllTicketByEvent,
    searchBookings,
    searchBookingsByContact,
    getAllBookingsWithFilters,
    getAllBookingByOrganizerId
} from "api";
import Breadcrumbs from "components/Common/Breadcrumb";
import Select from "react-select";
import TableContainer from "../../../components/Common/TableContainer";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { calcRevenueDetails, calculateSummaryStats } from "utils/revenueCalculator";

// Date picker imports
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";

// AG Grid imports
import { AgGridReact } from 'ag-grid-react';
import jsPDF from "jspdf";
import 'jspdf-autotable';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { filter } from "lodash";

const RunReport = (props) => {
    document.title = "Run Report | EventoX";
    const navigate = useNavigate();

    // State for search criteria
    const [searchCriteria, setSearchCriteria] = useState({
        fromDate: null,
        toDate: null,
        selectedEvent: null,
        selectedEvents: [], // For multiple event selection
        selectedTicketType: null,
        selectedVenue: null,
        selectedOrganizer: null,
        emailOrPhone: "",
        searchType: "date" // date, event, multiple_event, ticket, venue, organizer, email
    });

    // State for data
    const [eventList, setEventList] = useState([]);
    const [organizerList, setOrganizerList] = useState([]);
    const [venueList, setVenueList] = useState([]);
    const [ticketTypeList, setTicketTypeList] = useState([]);
    const [bookingList, setBookingList] = useState([]);
    const [filteredBookingList, setFilteredBookingList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isViewAllAttendees, setIsViewAllAttendees] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [isMixedCurrency, setIsMixedCurrency] = useState(false);
    const gridRef = useRef(null);

    // Debug: Log state changes
    useEffect(() => {
    }, [isViewAllAttendees]);

    // Reset event list when search type changes to multiple_event
    useEffect(() => {
        if (searchCriteria.searchType === 'multiple_event') {
            fetchInitialData();
        }
    }, [searchCriteria.searchType]);

    // State for column selection
    const [selectedColumns, setSelectedColumns] = useState([
        'bookingNo', 'name', 'email', 'phone', 'ticketType', 'ticketCount',
        'ticketAmount', 'grandTotal', 'paymentStatus', 'bookingStatus',
        'eventName', 'organizerName', 'venueName', 'createdAt', 'refundAmount'
    ]);

    // State for summary statistics
    const [summaryStats, setSummaryStats] = useState({
        totalBookings: 0,
        totalRevenue: 0,
        totalTickets: 0,
        averageTicketPrice: 0,
        eventoxRevenue: 0,
        organizerRevenue: 0,
        stripeRevenue: 0,
        confirmedBookings: 0,
        cancelledBookings: 0,
        pendingBookings: 0,
        totalDiscount: 0,
        agentFee:0,
        totalRefundedAmount: 0,
        refundedBookingsCount: 0,
        totalRefundedTickets: 0,
        stripeRefundedAmount: 0,
        eventoxRefundedAmount: 0,
        organizerRefundedAmount: 0,
        agentFeeRefundedAmount: 0
    });

    // State for state restoration notification
    const [stateRestored, setStateRestored] = useState(false);

    // Available columns for selection
    const availableColumns = [
        { value: 'bookingNo', label: 'Booking No' },
        { value: 'name', label: 'Name' },
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Phone' },
        { value: 'ticketType', label: 'Ticket Type' },
        { value: 'ticketCount', label: 'Ticket Count' },
        { value: 'ticketAmount', label: 'Ticket Amount' },
        { value: 'processingFee', label: 'Processing Fee' },
        { value: 'ticketFee', label: 'Ticket Fee' },
        { value: 'discountAmount', label: 'Discount Amount' },
        { value: 'grandTotal', label: 'Grand Total' },
        { value: 'currency', label: 'Currency' },
        { value: 'paymentStatus', label: 'Payment Status' },
        { value: 'bookingStatus', label: 'Booking Status' },
        { value: 'eventName', label: 'Event Name' },
        { value: 'organizerName', label: 'Organizer Name' },
        { value: 'venueName', label: 'Venue Name' },
        { value: 'couponCode', label: 'Coupon Code' },
        { value: 'checkIn', label: 'Check In' },
        { value: 'createdAt', label: 'Created Date' },
        { value: 'stripeRevenue', label: 'Stripe Revenue' },
        { value: 'eventoxRevenue', label: 'Eventox Revenue' },
        { value: 'organizerRevenue', label: 'Organizer Revenue' },
        { value: 'agentFee', label: 'Agent Fee' },
        { value: 'platformFee', label: 'Platform Fee' },
        { value: 'refundAmount', label: 'Refund Amount' }
    ];

    // Save search state to sessionStorage
    const saveSearchState = () => {
        try {
            const searchState = {
                searchCriteria,
                bookingList,
                filteredBookingList,
                summaryStats,
                selectedColumns,
                isViewAllAttendees
            };
            sessionStorage.setItem('runReportSearchState', JSON.stringify(searchState));
        } catch (error) {
            console.error('Failed to save full search state to sessionStorage:', error);
            // If it fails, try saving only the criteria and stats (essential info)
            try {
                const minimalState = {
                    searchCriteria,
                    summaryStats,
                    selectedColumns,
                    isViewAllAttendees
                };
                sessionStorage.setItem('runReportSearchState', JSON.stringify(minimalState));
            } catch (innerError) {
                console.error('Failed to save even minimal search state:', innerError);
            }
        }
    };

    // Clear saved search state
    const clearSearchState = () => {
        sessionStorage.removeItem('runReportSearchState');
    };

    // Navigate to booking details with state preservation
    const navigateToBookingDetails = (bookingNo) => {
        saveSearchState();
        window.open(
            `/admin/report/booking-details/${bookingNo}`,
            "_blank"
          );
    };

    // Load initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Effect to restore state when component mounts
    useEffect(() => {
        // Check if we're returning from a details page
        const savedState = sessionStorage.getItem('runReportSearchState');
        if (savedState && (bookingList.length === 0 || !searchCriteria.fromDate)) {
            try {
                const parsedState = JSON.parse(savedState);
                if (parsedState.searchCriteria) {
                    setSearchCriteria(parsedState.searchCriteria);
                    if (parsedState.bookingList && parsedState.bookingList.length > 0) {
                        setBookingList(parsedState.bookingList);
                        setFilteredBookingList(parsedState.filteredBookingList);
                        setSummaryStats(parsedState.summaryStats);
                        setSelectedColumns(parsedState.selectedColumns);
                        if (parsedState.isViewAllAttendees !== undefined) {
                            setIsViewAllAttendees(parsedState.isViewAllAttendees);
                        }
                        setStateRestored(true);
                        // Clear the notification after 3 seconds
                        setTimeout(() => setStateRestored(false), 3000);
                    }
                }
            } catch (e) {
                console.error('Error restoring search state:', e);
            }
        }
    }, []);

    // Fetch initial data (events and organizers)
    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [eventsResp, organizersResp] = await Promise.all([
                getAllEvents(),
                getAllOrganizer()
            ]);

            const eventOptions = eventsResp.map(item => ({
                value: item.eventId,
                label: item.eventName,
                slugId: item.slugId
            }));

            const organizerOptions = organizersResp.map(item => ({
                value: item.organizerId,
                label: item.name
            }));

            setEventList(eventOptions);
            setOrganizerList(organizerOptions);
        } catch (error) {
            console.error("Error fetching initial data:", error);
            setError("Failed to load initial data");
        } finally {
            setLoading(false);
        }
    };

    // Handle event selection
    const handleEventChange = async (selectedOption) => {
        setSearchCriteria(prev => ({ ...prev, selectedEvent: selectedOption }));

        if (selectedOption) {
            try {
                // Fetch venues and ticket types for the selected event
                const [venuesResp, ticketsResp] = await Promise.all([
                    getEventVenuesBySlug(selectedOption.slugId),
                    getAllTicketByEvent(selectedOption.slugId)
                ]);

                const venueOptions = venuesResp.map(item => ({
                    value: item.venueId,
                    label: item.venueName
                }));

                const ticketOptions = ticketsResp.map(item => ({
                    value: item.ticketTypeId,
                    label: item.ticketType,
                    price: item.price
                }));

                setVenueList(venueOptions);
                setTicketTypeList(ticketOptions);
            } catch (error) {
                console.error("Error fetching event data:", error);
            }
        } else {
            setVenueList([]);
            setTicketTypeList([]);
        }
    };

    // Handle organizer selection
    const handleOrganizerChange = async (selectedOption) => {
        setSearchCriteria(prev => ({ ...prev, selectedOrganizer: selectedOption }));

        if (selectedOption) {
            try {
                // For multiple_event, don't filter events by organizer - show all events
                if (searchCriteria.searchType === 'multiple_event') {
                    fetchInitialData();
                } else {
                    // Filter events by organizer for other search types
                    const eventsResp = await getAllEvents();
                    const organizerEvents = eventsResp.filter(event =>
                        event.organizerId === selectedOption.value
                    );

                    const eventOptions = organizerEvents.map(item => ({
                        value: item.eventId,
                        label: item.eventName,
                        slugId: item.slugId
                    }));

                    setEventList(eventOptions);
                }
            } catch (error) {
                console.error("Error fetching organizer events:", error);
            }
        } else {
            // Always fetch all events when organizer is cleared
            fetchInitialData();
        }
    };
    const getVisibleRows = () => {
        const rows = [];
        if (!gridRef.current) return rows;
      
        gridRef.current.api.forEachNodeAfterFilterAndSort(node => {
          rows.push(node.data);
        });
      
        return rows;
      };
      const handleGridFilterChange = () => {
        const visibleRows = getVisibleRows();
        const stats = calculateSummaryStats(visibleRows);
        setSummaryStats(stats);
    };
    const clearGridFilters = () => {
        if (!gridRef.current) return;
      
        gridRef.current.api.setFilterModel(null);   
        gridRef.current.api.onFilterChanged();      
        const visibleRows = getVisibleRows();
        setSummaryStats(calculateSummaryStats(visibleRows));
      };
    // Search bookings based on criteria
    const performBookingSearch = async () => {
        try {
            setLoading(true);
            setError("");

            let bookings = [];
            let afterProcessingBookings = [];

            switch (searchCriteria.searchType) {
                case 'date':
                    if (searchCriteria.fromDate && searchCriteria.toDate) {
                        try {
                            const response = await getAllBookingByDateRange(
                                moment(searchCriteria.fromDate).format('YYYY-MM-DD'),
                                moment(searchCriteria.toDate).format('YYYY-MM-DD')
                            );
                            bookings = response;
                        } catch (error) {
                            // Fallback to comprehensive search
                            const searchParams = {
                                fromDate: moment(searchCriteria.fromDate).format('YYYY-MM-DD'),
                                toDate: moment(searchCriteria.toDate).format('YYYY-MM-DD')
                            };
                            const response = await searchBookings(searchParams);
                            bookings = response;
                        }
                    }
                    break;

                case 'event':
                    if (searchCriteria.selectedEvent) {
                        try {
                            const response = await getBookingByEventId(searchCriteria.selectedEvent.value);
                            bookings = response;
                        } catch (error) {
                            // Fallback to comprehensive search
                            const searchParams = {
                                eventId: searchCriteria.selectedEvent.value
                            };
                            const response = await searchBookings(searchParams);
                            bookings = response;
                        }
                    }
                    break;

                case 'multiple_event':
                    if (searchCriteria.selectedEvents && searchCriteria.selectedEvents.length > 0) {
                        try {
                            // Fetch bookings for all selected events
                            const eventIds = searchCriteria.selectedEvents.map(event => event.value);
                            const allBookings = [];
                            
                            for (const eventId of eventIds) {
                                try {
                                    const response = await getBookingByEventId(eventId);
                                    if (response && Array.isArray(response)) {
                                        allBookings.push(...response);
                                    }
                                } catch (error) {
                                    console.error(`Error fetching bookings for event ${eventId}:`, error);
                                }
                            }
                            
                            bookings = allBookings;
                        } catch (error) {
                            console.error("Error fetching multiple event bookings:", error);
                            // Fallback to comprehensive search
                            const eventIds = searchCriteria.selectedEvents.map(event => event.value);
                            const searchParams = {
                                eventIds: eventIds
                            };
                            try {
                                const response = await searchBookings(searchParams);
                                bookings = response;
                            } catch (fallbackError) {
                                console.error("Fallback search also failed:", fallbackError);
                                bookings = [];
                            }
                        }
                    }
                    break;

                case 'ticket':
                    if (searchCriteria.selectedEvent && searchCriteria.selectedTicketType) {
                        try {
                            const response = await getBookingByEventId(searchCriteria.selectedEvent.value);
                            bookings = response.filter(booking =>
                                Number(booking.ticketTypeId) === Number(searchCriteria.selectedTicketType.value)
                            );
                        } catch (error) {
                            // Fallback to comprehensive search
                            const searchParams = {
                                eventId: searchCriteria.selectedEvent.value,
                                ticketTypeId: searchCriteria.selectedTicketType.value
                            };
                            const response = await searchBookings(searchParams);
                            bookings = response;
                        }
                    }
                    break;

                case 'venue':
                    if (searchCriteria.selectedEvent && searchCriteria.selectedVenue) {
                        try {
                            const response = await getBookingByEventId(searchCriteria.selectedEvent.value);
                            bookings = response.filter(booking =>
                                booking.venueId === searchCriteria.selectedVenue.value
                            );
                        } catch (error) {
                            // Fallback to comprehensive search
                            const searchParams = {
                                eventId: searchCriteria.selectedEvent.value,
                                venueId: searchCriteria.selectedVenue.value
                            };
                            const response = await searchBookings(searchParams);
                            bookings = response;
                        }
                    }
                    break;

                case 'organizer':
                    if (searchCriteria.selectedOrganizer) {
                        try {
                            const response = await getAllBookingByOrganizerId(searchCriteria.selectedOrganizer.value);
                            bookings = response;
                        } catch (error) {
                            console.error("Error fetching organizer bookings:", error);
                            // Fallback to comprehensive search
                            bookings = [];
                        }
                    }
                    break;

                case 'email':
                    if (searchCriteria.emailOrPhone.trim()) {
                        try {
                            const response = await searchBookingsByContact(searchCriteria.emailOrPhone);
                            if(response.success){
                                bookings = response.data;
                            }else{
                                bookings = [];
                            }
                        } catch (error) {
                            // Fallback to client-side filtering
                            const allBookings = await getAllBookingByOrganizer();
                            bookings = allBookings.filter(booking => {
                                const contact = booking.Contact;
                                const bookingObject = booking.bookingObject;

                                if (!contact && !bookingObject) return false;

                                const searchTerm = searchCriteria.emailOrPhone.toLowerCase();

                                // Check contact details
                                if (contact) {
                                    if (contact.email?.toLowerCase().includes(searchTerm) ||
                                        contact.phoneNo?.toLowerCase().includes(searchTerm)) {
                                        return true;
                                    }
                                }

                                // Check booking object details
                                if (bookingObject?.primaryContactDetails) {
                                    const primary = bookingObject.primaryContactDetails;
                                    if (primary.email?.toLowerCase().includes(searchTerm) ||
                                        primary.phoneNo?.toLowerCase().includes(searchTerm)) {
                                        return true;
                                    }
                                }

                                return false;
                            });
                        }
                    }
                    break;

                default:
                    break;
            }
            // if view all attendees is true, then fetch all bookings
            if (isViewAllAttendees) {
                afterProcessingBookings = bookings.filter(booking => booking.bookedBy === '0' || booking.bookedBy === 0);
            } else {
                afterProcessingBookings = bookings;
            }

            setBookingList(bookings);
            setFilteredBookingList(afterProcessingBookings);

            const uniqueCurrencies = [...new Set(bookings.map(b => b.currency).filter(Boolean))];
            setCurrencies(uniqueCurrencies);
            setIsMixedCurrency(uniqueCurrencies.length > 1);

            // Calculate summary statistics
            updateSummaryStats(bookings);

            // Save state after successful search
            setTimeout(() => saveSearchState(), 100);
        } catch (error) {
            console.error("Error searching bookings:", error);
            setError("Failed to search bookings. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Validate search criteria
    const isSearchValid = () => {
        switch (searchCriteria.searchType) {
            case 'date':
                return searchCriteria.fromDate && searchCriteria.toDate;
            case 'event':
                return searchCriteria.selectedEvent;
            case 'multiple_event':
                return searchCriteria.selectedEvents && searchCriteria.selectedEvents.length > 0;
            case 'ticket':
                return searchCriteria.selectedEvent && searchCriteria.selectedTicketType;
            case 'venue':
                return searchCriteria.selectedEvent && searchCriteria.selectedVenue;
            case 'organizer':
                return searchCriteria.selectedOrganizer;
            case 'email':
                return searchCriteria.emailOrPhone.trim().length > 0;
            default:
                return false;
        }
    };

    // Calculate summary statistics using utility function
    const updateSummaryStats = (bookings) => {
        const stats = calculateSummaryStats(bookings);
        setSummaryStats(stats);
    };

    const displayCurrency = filteredBookingList[0]?.currency || '';
    const formatCurrency = (amount, curr) => `${curr} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // AG Grid column definitions
    const columnDefs = useMemo(() => {
        const columns = [];

        if (selectedColumns.includes('bookingNo')) {
            columns.push({
                headerName: 'Booking No',
                field: 'bookingNo',
                filter: true,
                sortable: true,
                width: 150
            });
        }

        if (selectedColumns.includes('name')) {
            columns.push({
                headerName: 'Name',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                },
                filter: true,
                sortable: true,
                width: 150
            });
        }

        if (selectedColumns.includes('email')) {
            columns.push({
                headerName: 'Email',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return contact.email || '';
                },
                filter: true,
                sortable: true,
                width: 200
            });
        }

        if (selectedColumns.includes('phone')) {
            columns.push({
                headerName: 'Phone',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return contact.phoneNo || '';
                },
                filter: true,
                sortable: true,
                width: 120
            });
        }

        if (selectedColumns.includes('ticketType')) {
            columns.push({
                headerName: 'Ticket Type',
                valueGetter: params => params.data.TicketType?.ticketType || '',
                filter: true,
                sortable: true,
                width: 150
            });
        }

        if (selectedColumns.includes('ticketCount')) {
            columns.push({
                headerName: 'Ticket Count',
                field: 'totalTicketCount',
                filter: true,
                sortable: true,
                width: 100
            });
        }

        if (selectedColumns.includes('ticketAmount')) {
            columns.push({
                headerName: 'Ticket Amount',
                field: 'totalTicketAmount',
                valueGetter: params => Number(params.data.totalTicketAmount || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('processingFee')) {
            columns.push({
                headerName: 'Processing Fee',
                field: 'processingFee',
                valueGetter: params => Number(params.data.processingFee || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('ticketFee')) {
            columns.push({
                headerName: 'Ticket Fee',
                field: 'ticketFee',
                valueGetter: params => Number(params.data.ticketFee || 0),
                filter: true,
                sortable: true,
                width: 100,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('platformFee')) {

            columns.push({
                headerName: 'Platform Fee',
                field: 'platformFee',
                valueGetter: params => Number(params.data.platformFee || 0),
                filter: true,
                sortable: true,
                width: 100,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }
        if (selectedColumns.includes('agentFee')) {

            columns.push({
                headerName: 'Agent Fee',
                field: 'agentFee',
                valueGetter: params => Number(params.data.agentFee || 0),
                filter: true,
                sortable: true,
                width: 100,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('discountAmount')) {
            columns.push({
                headerName: 'Discount Amount',
                field: 'discountAmount',
                valueGetter: params => Number(params.data.discountAmount || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('grandTotal')) {
            columns.push({
                headerName: 'Grand Total',
                field: 'grandTotal',
                valueGetter: params => Number(params.data.grandTotal || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        if (selectedColumns.includes('currency')) {
            columns.push({
                headerName: 'Currency',
                field: 'currency',
                filter: true,
                sortable: true,
                width: 80
            });
        }

        if (selectedColumns.includes('paymentStatus')) {
            columns.push({
                headerName: 'Payment Status',
                field: 'paymentStatus',
                filter: true,
                sortable: true,
                width: 120,
                cellRenderer: params => {
                    const status = params.value;
                    let badgeClass = 'bg-secondary';
                    if (status === 'paid') badgeClass = 'bg-success';
                    else if (status === 'pending') badgeClass = 'bg-warning';
                    else if (status === 'cancelled') badgeClass = 'bg-danger';
                    else if (status === 'expired') badgeClass = 'bg-warning';
                    else if (status === 'cancelled_by_user') badgeClass = 'bg-danger';



                    return <span className={`badge ${badgeClass}`}>{status}</span>;
                }
            });
        }

        if (selectedColumns.includes('bookingStatus')) {
            columns.push({
                headerName: 'Booking Status',
                field: 'bookingStatus',
                filter: true,
                sortable: true,
                width: 120,
                cellRenderer: params => {
                    const status = params.value;
                    let badgeClass = 'bg-secondary';
                    if (status === 'confirmed') badgeClass = 'bg-success';
                    else if (status === 'pending') badgeClass = 'bg-warning';
                    else if (status === 'cancelled') badgeClass = 'bg-danger';
                    else if (status === 'expired') badgeClass = 'bg-danger';
                    else if (status === 'cancelled_by_user') badgeClass = 'bg-danger';
                    else if (status === 'cancelled_by_organizer') badgeClass = 'bg-danger';
                    else if (status === 'cancelled_by_admin') badgeClass = 'bg-danger';
                    else if (status === 'cancelled_by_system') badgeClass = 'bg-danger';
                    else if (status === 'cancelled_by_system') badgeClass = 'bg-danger';

                    return <span className={`badge ${badgeClass}`}>{status}</span>;
                }
            });
        }

        if (selectedColumns.includes('eventName')) {
            columns.push({
                headerName: 'Event Name',
                valueGetter: params => {
                    const eventName = params.data.Event?.eventName || '';
                    // If multiple events are selected, make the event name more prominent
                    if (searchCriteria.searchType === 'multiple_event' && searchCriteria.selectedEvents.length > 1) {
                        return eventName;
                    }
                    return eventName;
                },
                filter: true,
                sortable: true,
                width: 200,
                cellRenderer: params => {
                    const eventName = params.value;
                    if (searchCriteria.searchType === 'multiple_event' && searchCriteria.selectedEvents.length > 1) {
                        return <span className="badge bg-primary">{eventName}</span>;
                    }
                    return eventName;
                }
            });
        }

        if (selectedColumns.includes('organizerName')) {
            columns.push({
                headerName: 'Organizer',
                valueGetter: params => params.data.Organizer?.name || '',
                filter: true,
                sortable: true,
                width: 150
            });
        }

        if (selectedColumns.includes('venueName')) {
            columns.push({
                headerName: 'Venue',
                valueGetter: params => params.data.Venue?.venueName || '',
                filter: true,
                sortable: true,
                width: 150
            });
        }

        if (selectedColumns.includes('couponCode')) {
            columns.push({
                headerName: 'Coupon Code',
                field: 'couponCode',
                filter: true,
                sortable: true,
                width: 100
            });
        }

        if (selectedColumns.includes('checkIn')) {
            columns.push({
                headerName: 'Check In',
                field: 'checkIn',
                filter: true,
                sortable: true,
                width: 100,
                cellRenderer: params => {
                    return params.value ?
                        <span className="badge bg-success">Yes</span> :
                        <span className="badge bg-secondary">No</span>;
                }
            });
        }

        if (selectedColumns.includes('createdAt')) {
            columns.push({
                headerName: 'Created Date',
                field: 'createdAt',
                filter: true,
                sortable: true,
                width: 150,
                valueFormatter: params => moment(params.value).format('MMM DD, YYYY HH:mm')
            });
        }

        if (selectedColumns.includes('stripeRevenue')) {
            columns.push({
                headerName: 'Stripe Revenue',
                field: 'stripeRevenue',
                valueGetter: params => Number(params.data.stripeRevenue || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`,
                cellStyle: { color: "#dc3545" },


            });
        }

        if (selectedColumns.includes('eventoxRevenue')) {
            columns.push({
                headerName: 'Eventox Revenue',
                field: 'eventoxRevenue',
                valueGetter: params => Number(params.data.eventoxRevenue || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`,
                cellStyle: { color: "#198754" },

            });
        }

        if (selectedColumns.includes('organizerRevenue')) {
            columns.push({
                headerName: 'Organizer Revenue',
                field: 'organizerRevenue',
                valueGetter: params => Number(params.data.organizerRevenue || 0),
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`,
                cellStyle: { color: "#0d6efd" },

            });
        }

        if (selectedColumns.includes('refundAmount')) {
            columns.push({
                headerName: 'Refund Amount',
                valueGetter: params => {
                    const refundInfo = calcRevenueDetails(params.data);
                    return Number(refundInfo.refundAmount || 0);
                },
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `${params.data?.currency || displayCurrency} ${params.value?.toFixed(2) || '0.00'}`
            });
        }

        // Always add Actions column
        columns.push({
            headerName: 'Actions',
            field: 'actions',
            width: 120,
            sortable: false,
            filter: false,
            cellRenderer: params => {
                return (
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => navigateToBookingDetails(params.data.primaryBookingNo)}
                        className="btn-sm"
                    >
                        <i className="bx bx-show me-1"></i>
                        View Details
                    </Button>
                );
            },
        });

        return columns;
    }, [selectedColumns, displayCurrency]);

    // AG Grid default column definition
    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: false,
        suppressMovable: false,
    }), []);

    // Export to CSV
    const exportToCSV = () => {
        if (!gridRef.current) return;
      
        gridRef.current.api.exportDataAsCsv({
          fileName: `booking_report_${moment().format("YYYY-MM-DD_HH-mm")}.csv`,
          onlyFiltered: true,   
          allColumns: false,
          processCellCallback: (params) => {
            if (typeof params.value === "string" && isNaN(Number(params.value))) {
              const num = parseFloat(params.value.replace(/[^0-9.-]/g, ""));
              return isNaN(num) ? params.value : num;
            }
            return params.value;
          }
        });
      };
      
      

    // Export to PDF
    const exportToPDF = () => {
        if (!gridRef.current) return;      
        const rowsData = getVisibleRows();
        if (!rowsData.length) return;
      
        const doc = new jsPDF({
          orientation: selectedColumns.length > 8 ? "landscape" : "portrait",
        });
      
        doc.setFontSize(16);
        doc.text("Booking Report", 14, 20);
        doc.setFontSize(10);
        doc.text(
          `Generated on: ${moment().format("MMM DD, YYYY HH:mm")}`,
          14,
          28
        );
        doc.text(`Total Records: ${rowsData.length}`, 14, 35);
      
        // Build headers from selected columns
        const headers = availableColumns
          .filter(col => selectedColumns.includes(col.value))
          .map(col => col.label);
      
        // Build row data
        const body = rowsData.map(booking =>
          selectedColumns.map(key => {
            switch (key) {
              case "bookingNo":
                return booking.bookingNo || "";
      
              case "name":
                const contact =
                  booking.bookingObject?.primaryContactDetails ||
                  booking.Contact ||
                  {};
                return `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
      
              case "email":
                return (
                  booking.bookingObject?.primaryContactDetails?.email ||
                  booking.Contact?.email ||
                  ""
                );
      
              case "phone":
                return (
                  booking.bookingObject?.primaryContactDetails?.phoneNo ||
                  booking.Contact?.phoneNo ||
                  ""
                );
      
              case "ticketType":
                return booking.TicketType?.ticketType || "";
      
              case "ticketCount":
                return booking.totalTicketCount ?? "";
      
              case "ticketAmount":
                return booking.totalTicketAmount?.toFixed(2) || "0.00";
      
              case "grandTotal":
                return booking.grandTotal?.toFixed(2) || "0.00";
      
              case "paymentStatus":
                return booking.paymentStatus || "";
      
              case "bookingStatus":
                return booking.bookingStatus || "";
      
              case "eventName":
                return booking.Event?.eventName || "";
      
              case "organizerName":
                return booking.Organizer?.name || "";
      
              case "venueName":
                return booking.Venue?.venueName || "";
      
              case "refundAmount":
                const refundInfoForPDF = calcRevenueDetails(booking);
                return Number(refundInfoForPDF.refundAmount).toFixed(2);
              case "createdAt":
                return booking.createdAt
                  ? moment(booking.createdAt).format("MMM DD, YYYY HH:mm")
                  : "";
      
              default:
                return booking[key] ?? "";
            }
          })
        );
      
        doc.autoTable({
          head: [headers],
          body,
          startY: 45,
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
          },
        });
      
        doc.save(
          `booking_report_${moment().format("YYYY-MM-DD_HH-mm")}.pdf`
        );
      };
      

    // Handle column selection
    const handleColumnSelection = (selectedOptions) => {
        const selectedValues = selectedOptions.map(option => option.value);
        setSelectedColumns(selectedValues);
    };
    const bookingStatusCounts = useMemo(() => {
        return bookingList.reduce(
          (acc, booking) => {
            const status = booking.bookingStatus?.toLowerCase();
      
            if (status === "failed") acc.failed++;
            if (status === "expired") acc.expired++;
            if (status === "confirmed") acc.confirmed++;
      
            return acc;
          },
          {
            confirmed: 0,
            failed: 0,
            expired: 0,
          }
        );
      }, [bookingList]);      
      

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Reports" breadcrumbItem="Run Report" />

                    {/* Search Form */}
                    <Card>
                        <CardBody>
                            <h4 className="card-title mb-4">Search Criteria</h4>
                            <p className="text-muted mb-4">
                                Select a search type and provide the required criteria to find bookings.
                                You can search by date range, single event, multiple events, ticket type, venue, organizer, or email/phone.
                                <strong>Multiple Events</strong> allows you to select multiple events and generate a combined report with breakdown by event.
                            </p>

                            <Form>
                                <Row>
                                    {/* Search Type Selection */}
                                    <Col lg={3} md={6}>
                                        <FormGroup>
                                            <Label>Search Type</Label>
                                            <Select
                                                value={{
                                                    value: searchCriteria.searchType,
                                                    label: searchCriteria.searchType.charAt(0).toUpperCase() + searchCriteria.searchType.slice(1)
                                                }}
                                                onChange={(option) => {
                                                    setSearchCriteria(prev => ({
                                                        ...prev,
                                                        searchType: option.value,
                                                        selectedEvent: null,
                                                        selectedEvents: [],
                                                        selectedTicketType: null,
                                                        selectedVenue: null,
                                                        selectedOrganizer: null,
                                                        emailOrPhone: ""
                                                    }));
                                                    
                                                    // Reset event list to show all events when multiple_event is selected
                                                    if (option.value === 'multiple_event') {
                                                        fetchInitialData();
                                                    }
                                                }}
                                                options={[
                                                    { value: 'date', label: 'Date Range' },
                                                    { value: 'event', label: 'Event' },
                                                    {value:'multiple_event',label:'Multiple Events'},
                                                    { value: 'ticket', label: 'Ticket Type' },
                                                    { value: 'venue', label: 'Venue' },
                                                    { value: 'organizer', label: 'Organizer' },
                                                    { value: 'email', label: 'Email/Phone' }
                                                ]}
                                                placeholder="Select search type"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>

                                    {/* Date Range */}
                                    {searchCriteria.searchType === 'date' && (
                                        <>
                                            <Col lg={3} md={6}>
                                                <FormGroup>
                                                    <Label>From Date</Label>
                                                    <FlatPickr
                                                        className="form-control"
                                                        value={searchCriteria.fromDate}
                                                        onChange={(date) => setSearchCriteria(prev => ({
                                                            ...prev,
                                                            fromDate: date[0]
                                                        }))}
                                                        options={{
                                                            dateFormat: "Y-m-d",
                                                            placeholder: "Select from date"
                                                        }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col lg={3} md={6}>
                                                <FormGroup>
                                                    <Label>To Date</Label>
                                                    <FlatPickr
                                                        className="form-control"
                                                        value={searchCriteria.toDate}
                                                        onChange={(date) => setSearchCriteria(prev => ({
                                                            ...prev,
                                                            toDate: date[0]
                                                        }))}
                                                        options={{
                                                            dateFormat: "Y-m-d",
                                                            placeholder: "Select to date"
                                                        }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>
                                    )}

                                    {/* Organizer Selection */}
                                    {(searchCriteria.searchType === 'organizer' || searchCriteria.searchType === 'event' || searchCriteria.searchType === 'ticket' || searchCriteria.searchType === 'venue') && (
                                        <Col lg={3} md={6}>
                                            <FormGroup>
                                                <Label>Organizer</Label>
                                                <Select
                                                    value={searchCriteria.selectedOrganizer}
                                                    onChange={handleOrganizerChange}
                                                    options={organizerList}
                                                    placeholder="Select organizer"
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}

                                    {/* Single Event Selection */}
                                    {(searchCriteria.searchType === 'event' || searchCriteria.searchType === 'ticket' || searchCriteria.searchType === 'venue') && (
                                        <Col lg={3} md={6}>
                                            <FormGroup>
                                                <Label>Event</Label>
                                                <Select
                                                    value={searchCriteria.selectedEvent}
                                                    onChange={handleEventChange}
                                                    options={eventList}
                                                    placeholder="Select event"
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}

                                    {/* Multiple Event Selection */}
                                    {searchCriteria.searchType === 'multiple_event' && (
                                        <Col lg={6} md={12}>
                                            <FormGroup>
                                                <Label>Select Multiple Events</Label>
                                                <Select
                                                    isMulti
                                                    value={searchCriteria.selectedEvents}
                                                    onChange={(selectedOptions) => setSearchCriteria(prev => ({
                                                        ...prev,
                                                        selectedEvents: selectedOptions || []
                                                    }))}
                                                    options={eventList}
                                                    placeholder="Select multiple events"
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                                <small className="text-muted">
                                                    You can select multiple events to generate a combined report
                                                </small>
                                            </FormGroup>
                                        </Col>
                                    )}

                                    {/* Ticket Type Selection */}
                                    {searchCriteria.searchType === 'ticket' && (
                                        <Col lg={3} md={6}>
                                            <FormGroup>
                                                <Label>Ticket Type</Label>
                                                <Select
                                                    value={searchCriteria.selectedTicketType}
                                                    onChange={(option) => setSearchCriteria(prev => ({
                                                        ...prev,
                                                        selectedTicketType: option
                                                    }))}
                                                    options={ticketTypeList}
                                                    placeholder="Select ticket type"
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}

                                    {/* Venue Selection */}
                                    {searchCriteria.searchType === 'venue' && (
                                        <Col lg={3} md={6}>
                                            <FormGroup>
                                                <Label>Venue</Label>
                                                <Select
                                                    value={searchCriteria.selectedVenue}
                                                    onChange={(option) => setSearchCriteria(prev => ({
                                                        ...prev,
                                                        selectedVenue: option
                                                    }))}
                                                    options={venueList}
                                                    placeholder="Select venue"
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}

                                    {/* Email/Phone Search */}
                                    {searchCriteria.searchType === 'email' && (
                                        <Col lg={3} md={6}>
                                            <FormGroup>
                                                <Label>Email or Phone</Label>
                                                <Input
                                                    type="text"
                                                    value={searchCriteria.emailOrPhone}
                                                    onChange={(e) => setSearchCriteria(prev => ({
                                                        ...prev,
                                                        emailOrPhone: e.target.value
                                                    }))}
                                                    placeholder="Enter email or phone number"
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}


                                </Row>
                                <Row className="align-items-center">
                                    {/* View All Attendees Checkbox */}
                                    <Col lg={3} md={6}>
                                        <FormGroup>
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    id="viewAllAttendees"
                                                    value={isViewAllAttendees}
                                                    onChange={(e) => {
                                                        setIsViewAllAttendees(!isViewAllAttendees);
                                                    }}
                                                    className="form-check-input me-2"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Label
                                                    className="form-check-label ms-2"
                                                    for="viewAllAttendees"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <i className="bx bx-group me-2 text-primary"></i>
                                                        <span className="fw-semibold">View Primary Attendees</span>
                                                        <Badge color="info" className="ms-2">Filter</Badge>
                                                    </div>
                                                </Label>
                                            </div>
                                            <small className="text-muted">
                                                When checked, only show the primary attendee otherwise show all attendees
                                            </small>
                                        </FormGroup>
                                    </Col>

                                    {/* Search Button */}
                                    <Col lg={2} md={6} className="d-flex align-items-end">
                                        <Button
                                            color="primary"
                                            onClick={performBookingSearch}
                                            disabled={loading || !isSearchValid()}
                                            className="w-100"
                                        >
                                            {loading ? 'Searching...' : 'Search'}
                                        </Button>
                                    </Col>
                                    <Col lg={1} md={6} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                setSearchCriteria({
                                                    fromDate: null,
                                                    toDate: null,
                                                    selectedEvent: null,
                                                    selectedEvents: [],
                                                    selectedTicketType: null,
                                                    selectedVenue: null,
                                                    selectedOrganizer: null,
                                                    emailOrPhone: "",
                                                    searchType: "date"
                                                });
                                                setFilteredBookingList([]);
                                                setBookingList([]);
                                                setError("");
                                                setStateRestored(false);
                                                setIsViewAllAttendees(false);
                                                clearSearchState();
                                            }}
                                            className="w-100"
                                        >
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>

                    {/* State Restored Notification */}
                    {stateRestored && (
                        <Alert color="info" className="mt-3">
                            <i className="bx bx-refresh me-2"></i>
                            Previous search results have been restored. You can continue from where you left off.
                        </Alert>
                    )}

                    {/* Results Section */}
                    {filteredBookingList.length > 0 && (
                        <>
                            {/* Summary Statistics */}
                            <Card className="mb-3">
                                <CardBody>
                                    <h5 className="card-title mb-4">
                                        <i className="bx bx-bar-chart-alt-2 me-2 text-primary"></i>
                                        Summary Statistics
                                    </h5>

                                    {/* Main Statistics */}
                                    {isMixedCurrency ? (
                                        <div className="mb-4">
                                            {currencies.map(curr => {
                                                const currencyBookings = filteredBookingList.filter(b => b.currency === curr);
                                                const stats = calculateSummaryStats(currencyBookings);
                                                return (
                                                    <div key={curr} className="border rounded p-3 mb-3">
                                                        <h6 className="fw-bold text-primary mb-3">{curr} Breakdown</h6>
                                                        <Row>
                                                            <Col lg={3}>
                                                                <div className="text-center">
                                                                    <h5 className="fw-bold">{stats.totalBookings}</h5>
                                                                    <small className="text-muted">Total Tickets</small>
                                                                </div>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="text-center">
                                                                    <h5 className="fw-bold text-success">{formatCurrency(stats.totalRevenue, curr)}</h5>
                                                                    <small className="text-muted">Total Collected</small>
                                                                </div>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="text-center">
                                                                    <h5 className="fw-bold text-info">{stats.totalTickets}</h5>
                                                                    <small className="text-muted">Total Attendees</small>
                                                                </div>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="text-center">
                                                                    <h5 className="fw-bold text-warning">{formatCurrency(stats.totalDiscount, curr)}</h5>
                                                                    <small className="text-muted">Total Discount</small>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                    <Row className="mb-4">
                                        <Col lg={3} md={6} className="mb-3">
                                            <div className="border rounded p-3 text-center h-100">
                                                <div className="text-primary mb-2">
                                                    <i className="bx bx-calendar-check fs-2"></i>
                                                </div>
                                                <h4 className="text-dark mb-1 fw-bold">{summaryStats?.totalBookings?.toLocaleString()}</h4>
                                                <p className="text-muted mb-0">Total Tickets</p>
                                            </div>
                                        </Col>
                                        <Col lg={3} md={6} className="mb-3">
                                            <div className="border rounded p-3 text-center h-100">
                                                <div className="text-success mb-2">
                                                    <i className="bx bx-dollar-circle fs-2"></i>
                                                </div>
                                                <h4 className="text-dark mb-1 fw-bold">{displayCurrency} {summaryStats?.totalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                <p className="text-muted mb-0">Total Collected Amount</p>
                                            </div>
                                        </Col>
                                        <Col lg={3} md={6} className="mb-3">
                                            <div className="border rounded p-3 text-center h-100">
                                                <div className="text-info mb-2">
                                                    <i className="bx bx-tickets fs-2"></i>
                                                </div>
                                                <h4 className="text-dark mb-1 fw-bold">{summaryStats?.totalTickets?.toLocaleString()}</h4>
                                                <p className="text-muted mb-0">Total Attendee</p>
                                            </div>
                                        </Col>
                                        <Col lg={3} md={6} className="mb-3">
                                            <div className="border rounded p-3 text-center h-100">
                                                <div className="text-warning mb-2">
                                                    <i className="bx bx-discount fs-2"></i>
                                                </div>
                                                <h4 className="text-dark mb-1 fw-bold">{displayCurrency} {summaryStats?.totalDiscount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                <p className="text-muted mb-0">Total Discount</p>
                                            </div>
                                        </Col>
                                    </Row>
                                    )}

                                    {/* Revenue Breakdown */}
                                    <div className="border rounded p-4 bg-light mb-4">
                                        <h6 className="text-dark mb-3 fw-bold">
                                            <i className="bx bx-dollar-circle me-2 text-success"></i>
                                            Revenue Breakdown
                                        </h6>
                                        <Row>
                                            <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-success rounded-circle p-2 me-3">
                                                        <i className="bx bx-trending-up text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-success">
                                                            {displayCurrency} {(summaryStats?.eventoxRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </h5>
                                                        <p className="mb-0 text-muted small">Eventox Revenue</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-primary rounded-circle p-2 me-3">
                                                        <i className="bx bx-user text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-primary">
                                                            {displayCurrency} {(summaryStats?.organizerRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </h5>
                                                        <p className="mb-0 text-muted small">Organizer Revenue</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-danger rounded-circle p-2 me-3">
                                                        <i className="bx bx-credit-card text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-danger">
                                                            {displayCurrency} {(summaryStats?.stripeRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </h5>
                                                        <p className="mb-0 text-muted small">Stripe Revenue</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-danger rounded-circle p-2 me-3">
                                                        <i className="bx bx-credit-card text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-danger">
                                                            {displayCurrency} {(summaryStats?.agentFee || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </h5>
                                                        <p className="mb-0 text-muted small">Agent Fee</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Refund Breakdown */}
                                    {summaryStats.refundedBookingsCount > 0 && (
                                        <div className="border rounded p-4 bg-light mb-4">
                                            <h6 className="text-dark mb-3 fw-bold">
                                                <i className="bx bx-undo me-2 text-secondary"></i>
                                                Refund Breakdown
                                            </h6>
                                            <Row>
                                                <Col lg={3} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-secondary rounded-circle p-2 me-3">
                                                            <i className="bx bx-dollar text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-secondary">
                                                                {displayCurrency} {(summaryStats?.totalRefundedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Total Refunded</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={3} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-primary rounded-circle p-2 me-3">
                                                            <i className="bx bx-receipt text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-primary">
                                                                {(summaryStats?.totalRefundedTickets || 0).toLocaleString()}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Refunded Tickets</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={3} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-info rounded-circle p-2 me-3">
                                                            <i className="bx bx-group text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-info">
                                                                {(summaryStats?.refundedBookingsCount || 0).toLocaleString()}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Refunded Attendee</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={3} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-danger rounded-circle p-2 me-3">
                                                            <i className="bx bx-credit-card text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-danger">
                                                                {displayCurrency} {(summaryStats?.stripeRefundedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Stripe Refunded</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={4} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-success rounded-circle p-2 me-3">
                                                            <i className="bx bx-trending-up text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-success">
                                                                {displayCurrency} {(summaryStats?.eventoxRefundedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Eventox Refunded</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={4} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-primary rounded-circle p-2 me-3" style={{ opacity: 0.8 }}>
                                                            <i className="bx bx-user text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-primary">
                                                                {displayCurrency} {(summaryStats?.organizerRefundedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Organizer Refunded</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col lg={4} md={6} className="mb-3">
                                                    <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                        <div className="bg-warning rounded-circle p-2 me-3">
                                                            <i className="bx bx-user-voice text-white"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="mb-1 fw-bold text-warning">
                                                                {displayCurrency} {(summaryStats?.agentFeeRefundedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </h5>
                                                            <p className="mb-0 text-muted small">Agent Fee Refunded</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    {/* Status Breakdown */}
                                    <div className="border rounded p-4 bg-light">
                                        <h6 className="text-dark mb-3 fw-bold">Booking Status</h6>
                                        <Row>
                                            <Col lg={4} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-success rounded-circle p-2 me-3">
                                                        <i className="bx bx-check-circle text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-success">{summaryStats.confirmedBookings.toLocaleString()}</h5>
                                                        <p className="mb-0 text-muted small">Confirmed</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            {/* <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-warning rounded-circle p-2 me-3">
                                                        <i className="bx bx-time text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-warning">{summaryStats.pendingBookings.toLocaleString()}</h5>
                                                        <p className="mb-0 text-muted small">Pending</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={3} md={6} className="mb-3">
                                                <div className="d-flex align-items-center p-3 bg-white rounded border">
                                                    <div className="bg-danger rounded-circle p-2 me-3">
                                                        <i className="bx bx-x-circle text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold text-danger">{summaryStats.cancelledBookings.toLocaleString()}</h5>
                                                        <p className="mb-0 text-muted small">Cancelled</p>
                                                    </div>
                                                </div>
                                            </Col> */}
                                            <Col lg={4} md={6} className="mb-3">
                                        <div className="d-flex align-items-center p-3 bg-white rounded border">
                                            <div className="bg-danger rounded-circle p-2 me-3">
                                            <i className="bx bx-x-circle text-white"></i>
                                            </div>
                                            <div>
                                            <h5 className="mb-1 fw-bold text-danger">
                                                {bookingStatusCounts.failed.toLocaleString()}
                                            </h5>
                                            <p className="mb-0 text-muted small">Failed</p>
                                            </div>
                                        </div>
                                        </Col>
                                        <Col lg={4} md={6} className="mb-3">
                                        <div className="d-flex align-items-center p-3 bg-white rounded border">
                                            <div className="bg-warning rounded-circle p-2 me-3">
                                            <i className="bx bx-time text-white"></i>
                                            </div>
                                            <div>
                                            <h5 className="mb-1 fw-bold text-warning">
                                                {bookingStatusCounts.expired.toLocaleString()}
                                            </h5>
                                            <p className="mb-0 text-muted small">Expired</p>
                                            </div>
                                        </div>
                                        </Col>

                                        </Row>
                                    </div>

                                    {/* Event Breakdown for Multiple Events */}
                                    {searchCriteria.searchType === 'multiple_event' && searchCriteria.selectedEvents.length > 1 && filteredBookingList.length > 0 && (
                                        <div className="border rounded p-4 bg-light mt-4">
                                            <h6 className="text-dark mb-3 fw-bold">
                                                <i className="bx bx-bar-chart-alt-2 me-2 text-primary"></i>
                                                Breakdown by Events
                                            </h6>
                                            <Row>
                                                {searchCriteria.selectedEvents.map((event, index) => {
                                                    const eventBookings = filteredBookingList.filter(booking => 
                                                        booking.Event?.eventId === event.value
                                                    );
                                                    const eventStats = calculateSummaryStats(eventBookings);
                                                    
                                                    return (
                                                        <Col lg={4} md={6} className="mb-3" key={event.value}>
                                                            <div className="border rounded p-3 bg-white">
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <div className="bg-primary rounded-circle p-2 me-2">
                                                                        <i className="bx bx-calendar text-white"></i>
                                                                    </div>
                                                                    <h6 className="mb-0 fw-bold text-primary">{event.label}</h6>
                                                                </div>
                                                                <div className="row text-center">
                                                                    <div className="col-4">
                                                                        <h6 className="mb-1 text-success fw-bold">{eventStats.totalBookings}</h6>
                                                                        <small className="text-muted">Bookings</small>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <h6 className="mb-1 text-info fw-bold">{eventStats.totalTickets}</h6>
                                                                        <small className="text-muted">Tickets</small>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <h6 className="mb-1 text-warning fw-bold">{displayCurrency} {eventStats.totalRevenue?.toFixed(2)}</h6>
                                                                        <small className="text-muted">Revenue</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    )}

                                    {/* Additional Metrics */}
                                    {summaryStats.totalBookings > 0 && (
                                        <Row className="mt-4">
                                            <Col lg={4} md={6} className="mb-3">
                                                <div className="border rounded p-3 text-center bg-white">
                                                    <div className="text-primary mb-2">
                                                        <i className="bx bx-trending-up fs-3"></i>
                                                    </div>
                                                    <h6 className="mb-1 text-dark">Success Rate</h6>
                                                    <h5 className="text-primary mb-0 fw-bold">
                                                        {((summaryStats.confirmedBookings / summaryStats.totalBookings) * 100).toFixed(1)}%
                                                    </h5>
                                                </div>
                                            </Col>
                                            <Col lg={4} md={6} className="mb-3">
                                                <div className="border rounded p-3 text-center bg-white">
                                                    <div className="text-info mb-2">
                                                        <i className="bx bx-user-check fs-3"></i>
                                                    </div>
                                                    <h6 className="mb-1 text-dark">Avg. Tickets/Booking</h6>
                                                    <h5 className="text-info mb-0 fw-bold">
                                                        {(summaryStats.totalTickets / summaryStats.totalBookings).toFixed(1)}
                                                    </h5>
                                                </div>
                                            </Col>
                                            <Col lg={4} md={6} className="mb-3">
                                                <div className="border rounded p-3 text-center bg-white">
                                                    <div className="text-success mb-2">
                                                        <i className="bx bx-dollar fs-3"></i>
                                                    </div>
                                                    <h6 className="mb-1 text-dark">Revenue per Booking</h6>
                                                    <h5 className="text-success mb-0 fw-bold">
                                                        {displayCurrency} {(summaryStats.totalRevenue / summaryStats.totalBookings).toFixed(2)}
                                                    </h5>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Results Table */}
                            <Card>
                                <CardBody>
                                    <Row className="mb-3">
                                        <Col lg={6}>
                                            <h4 className="card-title">Search Results</h4>
                                            <p className="text-muted">
                                                Found {filteredBookingList.length} booking(s)
                                            </p>
                                        </Col>
                                        <Col lg={6} className="text-end">
                                            <Button
                                                color="success"
                                                className="me-2"
                                                onClick={exportToCSV}
                                            >
                                                <i className="bx bx-download me-1"></i>
                                                Export CSV
                                            </Button>
                                            <Button
                                                color="danger"
                                                onClick={exportToPDF}
                                            >
                                                <i className="bx bx-file-pdf me-1"></i>
                                                Export PDF
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Column Selection */}
                                    <Row className="mb-3">
                                        <Col lg={8}>
                                            <FormGroup>
                                                <Label>Select Columns to Display</Label>
                                                <Select
                                                    isMulti
                                                    value={availableColumns.filter(col => selectedColumns.includes(col.value))}
                                                    onChange={handleColumnSelection}
                                                    options={availableColumns}
                                                    placeholder="Select columns to display"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} className="d-flex align-items-end">
                                            <div className="d-flex gap-2">
                                                <Button
                                                    color="outline-primary"
                                                    size="sm"
                                                    onClick={() => setSelectedColumns(availableColumns.map(col => col.value))}
                                                >
                                                    Select All
                                                </Button>
                                                <Button
                                                    color="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setSelectedColumns(['bookingNo', 'name', 'email', 'ticketType', 'grandTotal', 'paymentStatus'])}
                                                >
                                                    Default
                                                </Button>
                                                <Button
                                                color="warning"
                                                className="me-2"
                                                onClick={clearGridFilters}
                                                >
                                                <i className="bx bx-reset me-1"></i>
                                                Clear Filters
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* AG Grid */}
                                    <div className="ag-theme-alpine" style={{ height: '600px', width: '100%', overflow: 'hidden' }}>
                                        <AgGridReact
                                            ref={gridRef}
                                            columnDefs={columnDefs}
                                            rowData={filteredBookingList}
                                            defaultColDef={defaultColDef}
                                            pagination={true}
                                            paginationPageSize={50}
                                            paginationPageSizeSelector={[25, 50, 100, 200]}
                                            domLayout="normal"
                                            suppressRowClickSelection={true}
                                            enableCellTextSelection={true}
                                            suppressCopyRowsToClipboard={false}
                                            enableRangeSelection={true}
                                            suppressColumnVirtualisation={false}
                                            suppressRowVirtualisation={false}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </>
                    )}

                    {/* Error Message */}
                    {error && (
                        <Alert color="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}

                    {/* No Results Message */}
                    {!loading && filteredBookingList.length === 0 && bookingList.length > 0 && (
                        <Alert color="info" className="mt-3">
                            No bookings found matching your search criteria.
                        </Alert>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withTranslation()(RunReport);